import React, { useState, useEffect, useCallback } from 'react';
import { botService, permissionService } from '../services';
import BackButton from '../components/BackButton';
import BilibiliQRLogin from '../components/BilibiliQRLogin';
import '../styles/App.css';
import '../styles/BilibiliLogin.css';

const BotConfig = () => {
  const [loading, setLoading] = useState(true);
  const [botStatus, setBotStatus] = useState({ connected: false, hasConfig: false });
  const [config, setConfig] = useState(null);
  const [userLimits, setUserLimits] = useState({ max_rooms: 0, max_qq_groups: 0, current_rooms: 0, isAdmin: false });
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // View state
  const [view, setView] = useState('list'); // 'list' or 'detail'
  const [selectedRoomIndex, setSelectedRoomIndex] = useState(null);
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);

  useEffect(() => {
    fetchBotData();
    // Poll bot status every 10 seconds
    const interval = setInterval(fetchBotStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchBotStatus = async () => {
    try {
      const status = await botService.getBotStatus();
      setBotStatus(status);
    } catch (error) {
      console.error('Error fetching bot status:', error);
    }
  };

  const fetchBotData = async () => {
    setLoading(true);
    setError('');
    try {
      const [status, configData, limits] = await Promise.all([
        botService.getBotStatus(),
        botService.getBotConfig(),
        permissionService.getMyLimits()
      ]);
      setBotStatus(status);
      setConfig(configData);
      setUserLimits(limits);
      if (!configData.rooms) {
        setConfig({ rooms: [] });
      }
    } catch (error) {
      console.error('Error fetching bot data:', error);
      setError(error.response?.data?.message || '无法连接到 Bot，请确保 Bot 已启动并连接到服务器');
      // Try to get status even if config fails
      try {
        const status = await botService.getBotStatus();
        setBotStatus(status);
      } catch (e) {
        console.error('Error fetching status:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async (updatedConfig) => {
    setSaveLoading(true);
    setError('');
    setSuccess('');

    try {
      await botService.updateBotConfig(updatedConfig);
      setConfig(updatedConfig);
      setSuccess('配置已成功更新！Bot 会自动重载配置。');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving config:', error);
      setError(error.response?.data?.message || '保存配置失败');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleToggleRoom = async (index) => {
    const updatedConfig = { ...config };
    updatedConfig.rooms[index].enabled = !updatedConfig.rooms[index].enabled;
    await handleSaveConfig(updatedConfig);
  };

  const handleAddRoom = async (newRoomId) => {
    // Check room limit (unless admin or unlimited)
    const currentRoomCount = config?.rooms?.length || 0;
    if (!userLimits.isAdmin && userLimits.max_rooms !== -1 && currentRoomCount >= userLimits.max_rooms) {
      setError(`您最多只能添加 ${userLimits.max_rooms} 个房间，当前已有 ${currentRoomCount} 个`);
      return;
    }
    
    const updatedConfig = { ...config };
    updatedConfig.rooms.push({
      uid: parseInt(newRoomId),
      enabled: false,
      auto_reply: { enabled: false, live_only: true, cooldown: 8, keywords: {}, medal_check_enabled: false, min_medal_level: 0, guard_check_enabled: false, allowed_guard_levels: [] },
      welcome: { enabled: false, live_only: true, cooldown: 5, template: "欢迎{user}肥来！", medal_check_enabled: false, min_medal_level: 0, guard_check_enabled: false, allowed_guard_levels: [] },
      gift_thank: { enabled: false, live_only: true, template: "感谢{user}投喂的{num}个{gift}~", template_blind_box_profit: "感谢{user}开出{gift}，赚了{profit}电池~欧皇附体！", template_blind_box_loss: "感谢{user}开出{gift}，亏了{profit}电池~下次一定回本！", min_total_coin: 0, cooldown: 0 },
      guard_thank: { enabled: false, live_only: true, template: "感谢 {user} 开通了 {num} 个{unit}的{guard_name} ~老板大气！", cooldown: 0 },
      periodic_messages: { enabled: false, live_only: true, tasks: [] },
      nickname: { enabled: false, live_only: true, medal_check_enabled: false, min_medal_level: 0, guard_check_enabled: false, allowed_guard_levels: [] },
      gift_query: { enabled: false, live_only: true, template_total: "{user} {time_label}共投喂了{num}个{gift}，共{value}电池", template_zero: "{user} {time_label}没有投喂过{gift}呢" },
      fans_medal_upgrade: { enabled: false, min_level_filter: 0, new_fan_level_threshold: 10 },
      leaderboard_pm: { enabled: false, send_image: true, send_text: false, send_mixed_leaderboard: false, min_battery: 0 },
      anchor_info: { uid: parseInt(newRoomId), uname: "", face: "", room_id: 0 },
      qq_notify_enabled: false,
      qq_targets: []
    });
    await handleSaveConfig(updatedConfig);
    setShowAddRoom(false);
    // Update current room count in limits
    setUserLimits(prev => ({ ...prev, current_rooms: prev.current_rooms + 1 }));
  };

  const handleDeleteRoom = async (index) => {
    const room = config.rooms[index];
    const roomName = room.anchor_info?.uname || `UID ${room.uid}`;
    if (!window.confirm(`确定要删除房间 ${roomName} 吗？此操作不可恢复！`)) {
      return;
    }
    const updatedConfig = { ...config };
    updatedConfig.rooms.splice(index, 1);
    await handleSaveConfig(updatedConfig);
    setDeleteMode(false);
    if (selectedRoomIndex === index) {
      setView('list');
      setSelectedRoomIndex(null);
    }
  };

  const handleUpdateRoom = async (index, updates) => {
    const updatedConfig = { ...config };
    updatedConfig.rooms[index] = { ...updatedConfig.rooms[index], ...updates };
    await handleSaveConfig(updatedConfig);
  };

  const handleReload = async () => {
    setError('');
    setSuccess('');
    try {
      await botService.reloadBotConfig();
      setSuccess('配置重载指令已发送！');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error reloading config:', error);
      setError(error.response?.data?.message || '重载配置失败');
    }
  };

  const handleRefresh = () => {
    fetchBotData();
  };

  if (loading) {
    return (
      <div className="container">
        <BackButton />
        <div className="admin-container">
          <h1>Bot 配置管理</h1>
          <div className="loading">加载中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <BackButton />
      <div className="admin-container">
        <div className="admin-header">
          <h1>Bot 配置管理</h1>
          <div className="bot-status-indicator">
            <span className={`status-dot ${botStatus.connected ? 'status-connected' : 'status-disconnected'}`}></span>
            <span className="status-text">
              {botStatus.connected ? 'Bot 已连接' : 'Bot 未连接'}
            </span>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        {!botStatus.connected && (
          <div className="alert alert-warning">
            ⚠️ Bot 未连接到服务器。请确保 Bot 程序正在运行并配置了正确的 WebSocket 地址。
          </div>
        )}

        <div className="bot-config-actions">
          <button 
            className="btn btn-primary" 
            onClick={handleRefresh}
            disabled={!botStatus.connected}
          >
            🔄 刷新配置
          </button>
          
          <button 
            className="btn btn-secondary" 
            onClick={handleReload}
            disabled={!botStatus.connected}
          >
            🔄 重载配置
          </button>

          <a href="/admin/bot/qq" className="btn btn-qq-config">
            📱 QQ 推送配置
          </a>

          {view === 'list' && (
            <div className="top-right-actions">
              <button 
                className={`btn ${deleteMode ? 'btn-secondary' : 'btn-danger'}`}
                onClick={() => setDeleteMode(!deleteMode)}
                disabled={!botStatus.connected || !config?.rooms?.length}
              >
                {deleteMode ? '✓ 完成删除' : '🗑️ 删除房间'}
              </button>
              
              <button 
                className="btn btn-success" 
                onClick={() => setShowAddRoom(true)}
                disabled={!botStatus.connected || (!userLimits.isAdmin && userLimits.max_rooms !== -1 && (config?.rooms?.length || 0) >= userLimits.max_rooms)}
                title={!userLimits.isAdmin && userLimits.max_rooms !== -1 && (config?.rooms?.length || 0) >= userLimits.max_rooms 
                  ? `已达到房间上限 (${userLimits.max_rooms})` 
                  : '添加新房间'}
              >
                ➕ 添加房间
              </button>
              
              {!userLimits.isAdmin && userLimits.max_rooms > 0 && (
                <div className="room-quota-badge">
                  <span className="quota-icon">🏠</span>
                  <span className="quota-label">房间配额</span>
                  <span className={`quota-value ${(config?.rooms?.length || 0) >= userLimits.max_rooms ? 'at-limit' : ''}`}>
                    {config?.rooms?.length || 0} / {userLimits.max_rooms}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {view === 'list' ? (
          <RoomListView 
            config={config}
            botStatus={botStatus}
            deleteMode={deleteMode}
            boundBilibiliUid={config?.userPermissions?.boundBilibiliUid}
            onSelectRoom={(index) => {
              setSelectedRoomIndex(index);
              setView('detail');
            }}
            onToggleRoom={handleToggleRoom}
            onDeleteRoom={handleDeleteRoom}
          />
        ) : (
          <RoomDetailView 
            room={config?.rooms[selectedRoomIndex]}
            roomIndex={selectedRoomIndex}
            onBack={() => {
              setView('list');
              setSelectedRoomIndex(null);
            }}
            onUpdate={(updates) => handleUpdateRoom(selectedRoomIndex, updates)}
            saveLoading={saveLoading}
          />
        )}

        {showAddRoom && (
          <AddRoomModal 
            onClose={() => setShowAddRoom(false)}
            onAdd={handleAddRoom}
          />
        )}
      </div>
    </div>
  );
};

// Room List View Component
const RoomListView = ({ config, botStatus, deleteMode, boundBilibiliUid, onSelectRoom, onToggleRoom, onDeleteRoom }) => {
  if (!config?.rooms?.length) {
    return (
      <div className="no-config">
        <p>暂无配置的房间</p>
        <p className="help-text">点击右上角"➕ 添加房间"按钮开始配置</p>
      </div>
    );
  }

  return (
    <div className="room-list">
      {config.rooms.map((room, index) => (
        <div key={index} className={`room-card ${room.enabled ? 'room-enabled' : 'room-disabled'} ${room.is_new ? 'room-new' : ''}`}>
          <div className="room-card-header">
            {/* 自动绑定房间标记 */}
            {room.is_bound_room && (
              <div className="new-room-badge" title="通过B站账号绑定自动添加的房间">
                🆕 自动绑定
              </div>
            )}
            {/* 圆形头像 */}
            {room.anchor_info?.face ? (
              <img 
                src={room.anchor_info.face} 
                alt={room.anchor_info?.uname || ''}
                className="anchor-avatar"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling && (e.target.nextElementSibling.style.display = 'flex');
                }}
              />
            ) : null}
            {!room.anchor_info?.face && (
              <div className="anchor-avatar-placeholder">
                👤
              </div>
            )}
            
            <div className="room-card-title-area">
              <div className="anchor-name">{room.anchor_info?.uname || `UID: ${room.uid}`}</div>
              <h3>房间 {room.anchor_info?.room_id || room.uid}</h3>
            </div>

            {deleteMode ? (
              // 检查是否是用户绑定的房间（锁定状态）
              room.uid === boundBilibiliUid ? (
                <span className="locked-room-badge" title="通过B站账号绑定的房间，不可删除">
                  🔒 已锁定
                </span>
              ) : (
                <button 
                  className="btn btn-danger btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteRoom(index);
                  }}
                >
                  🗑️ 删除
                </button>
              )
            ) : (
              <label className="toggle-switch" onClick={(e) => e.stopPropagation()}>
                <input 
                  type="checkbox" 
                  checked={room.enabled}
                  onChange={() => onToggleRoom(index)}
                  disabled={!botStatus.connected}
                />
                <span className="toggle-slider"></span>
              </label>
            )}
          </div>
          
          <div className="room-card-body" onClick={() => !deleteMode && onSelectRoom(index)}>
            <div className="room-features">
              <span className={`feature-badge ${room.auto_reply?.enabled ? 'feature-enabled' : 'feature-disabled'}`}>
                自动回复
              </span>
              <span className={`feature-badge ${room.welcome?.enabled ? 'feature-enabled' : 'feature-disabled'}`}>
                进场欢迎
              </span>
              <span className={`feature-badge ${room.gift_thank?.enabled ? 'feature-enabled' : 'feature-disabled'}`}>
                礼物感谢
              </span>
              <span className={`feature-badge ${room.guard_thank?.enabled ? 'feature-enabled' : 'feature-disabled'}`}>
                大航海感谢
              </span>
              <span className={`feature-badge ${room.periodic_messages?.enabled ? 'feature-enabled' : 'feature-disabled'}`}>
                定期发言
              </span>
              <span className={`feature-badge ${room.nickname?.enabled ? 'feature-enabled' : 'feature-disabled'}`}>
                自定义昵称
              </span>
              <span className={`feature-badge ${room.gift_query?.enabled ? 'feature-enabled' : 'feature-disabled'}`}>
                礼物查询
              </span>
              <span className={`feature-badge ${room.fans_medal_upgrade?.enabled ? 'feature-enabled' : 'feature-disabled'}`}>
                粉丝牌私信
              </span>
              <span className={`feature-badge ${room.qq_notify_enabled ? 'feature-qq-enabled' : 'feature-disabled'}`}>
                📱 QQ推送 {room.qq_targets?.length > 0 ? `(${room.qq_targets.length})` : ''}
              </span>
            </div>
            {!deleteMode && (
              <div className="room-card-footer">
                <span className="click-hint">点击配置 →</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Room Detail View Component
const RoomDetailView = ({ room, roomIndex, onBack, onUpdate, saveLoading }) => {
  const [editingFeature, setEditingFeature] = useState(null);
  const [showBilibiliLogin, setShowBilibiliLogin] = useState(false);
  const [bilibiliAccount, setBilibiliAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(true);

  // 获取房间B站账号信息
  const fetchBilibiliAccount = useCallback(async () => {
    if (!room?.uid) return;
    setAccountLoading(true);
    try {
      const result = await botService.getRoomBilibiliAccount(room.uid);
      if (result.exists) {
        setBilibiliAccount(result.account);
      } else {
        setBilibiliAccount(null);
      }
    } catch (error) {
      console.error('获取B站账号失败:', error);
      setBilibiliAccount(null);
    } finally {
      setAccountLoading(false);
    }
  }, [room?.uid]);

  // 组件挂载时获取账号信息
  useEffect(() => {
    fetchBilibiliAccount();
  }, [fetchBilibiliAccount]);

  if (!room) return null;

  const handleFeatureToggle = (featureName) => {
    const updates = { ...room };
    // 确保功能结构存在
    if (!updates[featureName]) {
      updates[featureName] = { enabled: false };
    }
    updates[featureName].enabled = !updates[featureName].enabled;
    onUpdate(updates);
  };

  const handleFeatureUpdate = (featureName, featureData) => {
    const updates = { ...room };
    updates[featureName] = featureData;
    onUpdate(updates);
    setEditingFeature(null);
  };

  // 处理B站登录成功
  const handleBilibiliLoginSuccess = async (loginData) => {
    try {
      console.log('保存B站账号:', { roomUid: room.uid, mid: loginData.mid, uname: loginData.uname });
      
      const result = await botService.saveRoomBilibiliAccount(room.uid, {
        cookie: loginData.cookie,
        mid: loginData.mid,
        uname: loginData.uname,
        face: loginData.face
      });
      
      console.log('保存结果:', result);
      
      // 更新本地状态
      setBilibiliAccount({
        mid: loginData.mid,
        uname: loginData.uname,
        face: loginData.face,
        has_cookie: true
      });
    } catch (error) {
      console.error('保存B站账号失败:', error);
      alert('保存账号失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 删除B站账号
  const handleDeleteBilibiliAccount = async () => {
    if (!window.confirm('确定要解绑此B站账号吗？解绑后将使用默认账号。')) {
      return;
    }
    try {
      await botService.deleteRoomBilibiliAccount(room.uid);
      setBilibiliAccount(null);
    } catch (error) {
      console.error('删除B站账号失败:', error);
    }
  };

  return (
    <div className="room-detail">
      <div className="room-detail-header">
        <button className="btn btn-secondary" onClick={onBack}>
          ← 返回房间列表
        </button>
        <h2>{room.anchor_info?.uname || `房间 ${room.anchor_info?.room_id || room.uid}`} 配置</h2>
      </div>

      {/* B站账号管理卡片 */}
      <div className="feature-card bilibili-account-card">
        <div className="feature-card-header">
          <div className="feature-card-title">
            <span className="feature-icon">🔗</span>
            <h3>B站 Bot 账号</h3>
          </div>
          <div className="feature-card-actions">
            {bilibiliAccount?.has_cookie && (
              <button 
                className="btn btn-sm btn-danger" 
                onClick={handleDeleteBilibiliAccount}
                title="解绑账号"
              >
                解绑
              </button>
            )}
            <button 
              className="btn btn-sm btn-primary" 
              onClick={() => setShowBilibiliLogin(true)}
            >
              {bilibiliAccount?.has_cookie ? '更换账号' : '绑定账号'}
            </button>
          </div>
        </div>
        <div className="feature-card-body">
          {accountLoading ? (
            <p className="loading-text">加载中...</p>
          ) : bilibiliAccount?.has_cookie ? (
            <div className="bilibili-account-info">
              {bilibiliAccount.face && (
                <img 
                  src={bilibiliAccount.face} 
                  alt="头像" 
                  className="account-avatar-small"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <div className="account-details">
                <span className="account-name">{bilibiliAccount.uname || '未知用户'}</span>
                <span className="account-mid">UID: {bilibiliAccount.mid}</span>
              </div>
              <span className="account-badge active">已绑定</span>
            </div>
          ) : (
            <div className="bilibili-account-empty">
              <p>未绑定B站账号，将使用默认账号</p>
              <p className="hint">绑定后，本房间的弹幕发送、直播互动等将使用您绑定的账号</p>
            </div>
          )}
        </div>
      </div>

      <FeatureCard
        title="自动回复"
        icon="💬"
        enabled={room.auto_reply?.enabled}
        onToggle={() => handleFeatureToggle('auto_reply')}
        onEdit={() => setEditingFeature('auto_reply')}
      >
        <div className="feature-summary">
          <p>关键词数量: {Object.keys(room.auto_reply?.keywords || {}).length}</p>
          <p>冷却时间: {room.auto_reply?.cooldown || 0} 秒</p>
          <p>仅开播时: {room.auto_reply?.live_only ? '是' : '否'}</p>
        </div>
      </FeatureCard>

      <FeatureCard
        title="进场欢迎"
        icon="👋"
        enabled={room.welcome?.enabled}
        onToggle={() => handleFeatureToggle('welcome')}
        onEdit={() => setEditingFeature('welcome')}
      >
        <div className="feature-summary">
          <p>欢迎模板: {room.welcome?.template}</p>
          <p>冷却时间: {room.welcome?.cooldown || 0} 秒</p>
        </div>
      </FeatureCard>

      <FeatureCard
        title="礼物感谢"
        icon="🎁"
        enabled={room.gift_thank?.enabled}
        onToggle={() => handleFeatureToggle('gift_thank')}
        onEdit={() => setEditingFeature('gift_thank')}
      >
        <div className="feature-summary">
          <p>感谢模板: {room.gift_thank?.template}</p>
          <p>最低价值: {room.gift_thank?.min_total_coin || 0} 电池</p>
        </div>
      </FeatureCard>

      <FeatureCard
        title="大航海感谢"
        icon="⚓"
        enabled={room.guard_thank?.enabled}
        onToggle={() => handleFeatureToggle('guard_thank')}
        onEdit={() => setEditingFeature('guard_thank')}
      >
        <div className="feature-summary">
          <p>感谢模板: {room.guard_thank?.template}</p>
        </div>
      </FeatureCard>

      <FeatureCard
        title="定期发言"
        icon="⏰"
        enabled={room.periodic_messages?.enabled}
        onToggle={() => handleFeatureToggle('periodic_messages')}
        onEdit={() => setEditingFeature('periodic_messages')}
      >
        <div className="feature-summary">
          <p>任务数量: {room.periodic_messages?.tasks?.length || 0}</p>
        </div>
      </FeatureCard>

      <FeatureCard
        title="昵称管理"
        icon="📝"
        enabled={room.nickname?.enabled}
        onToggle={() => handleFeatureToggle('nickname')}
        onEdit={() => setEditingFeature('nickname')}
      >
        <div className="feature-summary">
          <p>仅开播时: {room.nickname?.live_only ? '是' : '否'}</p>
          <p>需要粉丝牌: {room.nickname?.medal_check_enabled ? `是 (等级≥${room.nickname?.min_medal_level})` : '否'}</p>
        </div>
      </FeatureCard>

      <FeatureCard
        title="礼物查询"
        icon="🔍"
        enabled={room.gift_query?.enabled}
        onToggle={() => handleFeatureToggle('gift_query')}
        onEdit={() => setEditingFeature('gift_query')}
      >
        <div className="feature-summary">
          <p>仅开播时: {room.gift_query?.live_only ? '是' : '否'}</p>
        </div>
      </FeatureCard>

      <FeatureCard
        title="粉丝牌变动"
        icon="🏅"
        enabled={room.fans_medal_upgrade?.enabled}
        onToggle={() => handleFeatureToggle('fans_medal_upgrade')}
        onEdit={() => setEditingFeature('fans_medal_upgrade')}
        toggleLabel="私信"
      >
        <div className="feature-summary">
          <p>私信通知: {room.fans_medal_upgrade?.enabled ? '开启' : '关闭'}</p>
          <p>最低等级: {room.fans_medal_upgrade?.min_level_filter || '不过滤'}</p>
          <p>新粉阈值: {room.fans_medal_upgrade?.new_fan_level_threshold ?? 10}级</p>
        </div>
      </FeatureCard>

      <FeatureCard
        title="贡献榜私信"
        icon="📊"
        enabled={room.leaderboard_pm?.enabled}
        onToggle={() => handleFeatureToggle('leaderboard_pm')}
        onEdit={() => setEditingFeature('leaderboard_pm')}
        toggleLabel="私信"
      >
        <div className="feature-summary">
          <p>私信通知: {room.leaderboard_pm?.enabled ? '开启' : '关闭'}</p>
          <p>发送内容: {[
            room.leaderboard_pm?.send_image !== false ? '贡献榜' : '',
            room.leaderboard_pm?.send_mixed_leaderboard ? '总榜' : '',
            room.leaderboard_pm?.send_text ? '文字' : ''
          ].filter(Boolean).join('+') || '无'}</p>
          <p>最低电池: {room.leaderboard_pm?.min_battery || '不过滤'}</p>
        </div>
      </FeatureCard>

      {editingFeature && (
        <FeatureEditModal
          feature={editingFeature}
          data={room[editingFeature]}
          onClose={() => setEditingFeature(null)}
          onSave={(data) => handleFeatureUpdate(editingFeature, data)}
          saveLoading={saveLoading}
        />
      )}

      {/* B站扫码登录模态框 */}
      {showBilibiliLogin && (
        <div className="modal-overlay" onClick={() => setShowBilibiliLogin(false)}>
          <div className="modal-content bilibili-login-modal" onClick={(e) => e.stopPropagation()}>
            <BilibiliQRLogin
              currentAccount={bilibiliAccount}
              onLoginSuccess={handleBilibiliLoginSuccess}
              onCancel={() => setShowBilibiliLogin(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Feature Card Component
const FeatureCard = ({ title, icon, enabled, onToggle, onEdit, children, hideToggle, toggleLabel }) => {
  return (
    <div className={`feature-card ${enabled ? 'feature-card-enabled' : 'feature-card-disabled'}`}>
      <div className="feature-card-header">
        <div className="feature-card-title">
          <span className="feature-icon">{icon}</span>
          <h3>{title}</h3>
        </div>
        <div className="feature-card-actions">
          <button className="btn btn-sm btn-primary" onClick={onEdit}>
            ✏️ 编辑
          </button>
          {!hideToggle && (
            <div className="toggle-with-label">
              {toggleLabel && <span className="toggle-label">{toggleLabel}</span>}
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  checked={enabled}
                  onChange={onToggle}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          )}
        </div>
      </div>
      <div className="feature-card-body">
        {children}
      </div>
    </div>
  );
};

// Feature Edit Modal Component
const FeatureEditModal = ({ feature, data, onClose, onSave, saveLoading }) => {
  const [editData, setEditData] = useState(data);
  const [error, setError] = useState('');

  const handleSave = () => {
    onSave(editData);
  };

  const updateField = (field, value) => {
    setEditData({ ...editData, [field]: value });
  };

  const getFeatureTitle = (feature) => {
    const titles = {
      'auto_reply': '自动回复',
      'welcome': '进场欢迎',
      'gift_thank': '礼物感谢',
      'guard_thank': '大航海感谢',
      'periodic_messages': '定期发言',
      'nickname': '昵称管理',
      'gift_query': '礼物查询',
      'fans_medal_upgrade': '粉丝牌变动',
      'leaderboard_pm': '贡献榜私信'
    };
    return titles[feature] || feature;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>编辑 {getFeatureTitle(feature)}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          
          {feature === 'auto_reply' && (
            <AutoReplyForm data={editData} onChange={setEditData} />
          )}
          
          {feature === 'welcome' && (
            <WelcomeForm data={editData} onChange={setEditData} />
          )}
          
          {feature === 'gift_thank' && (
            <GiftThankForm data={editData} onChange={setEditData} />
          )}
          
          {feature === 'guard_thank' && (
            <GuardThankForm data={editData} onChange={setEditData} />
          )}
          
          {feature === 'periodic_messages' && (
            <PeriodicMessagesForm data={editData} onChange={setEditData} />
          )}
          
          {feature === 'nickname' && (
            <NicknameForm data={editData} onChange={setEditData} />
          )}
          
          {feature === 'gift_query' && (
            <GiftQueryForm data={editData} onChange={setEditData} />
          )}

          {feature === 'fans_medal_upgrade' && (
            <FansMedalUpgradeForm data={editData} onChange={setEditData} />
          )}

          {feature === 'leaderboard_pm' && (
            <LeaderboardPmForm data={editData} onChange={setEditData} />
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            取消
          </button>
          <button 
            className="btn btn-success" 
            onClick={handleSave}
            disabled={saveLoading}
          >
            {saveLoading ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Auto Reply Form Component
const AutoReplyForm = ({ data, onChange }) => {
  const updateField = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const updateKeyword = (oldKey, newKey, value) => {
    const keywords = { ...data.keywords };
    if (oldKey !== newKey) {
      delete keywords[oldKey];
    }
    keywords[newKey] = value;
    updateField('keywords', keywords);
  };

  const addKeyword = () => {
    const keywords = { ...data.keywords };
    keywords['新关键词'] = '回复内容';
    updateField('keywords', keywords);
  };

  const removeKeyword = (key) => {
    const keywords = { ...data.keywords };
    delete keywords[key];
    updateField('keywords', keywords);
  };

  return (
    <div className="config-form">
      <div className="form-section">
        <h4>基础设置</h4>
        
        <FormField label="启用状态" inline>
          <ToggleSwitch 
            checked={data.enabled}
            onChange={(checked) => updateField('enabled', checked)}
          />
        </FormField>

        <FormField label="仅在开播时启用" inline>
          <ToggleSwitch 
            checked={data.live_only}
            onChange={(checked) => updateField('live_only', checked)}
          />
        </FormField>

        <FormField label="冷却时间（秒）">
          <input
            type="number"
            className="form-input"
            value={data.cooldown || 0}
            onChange={(e) => updateField('cooldown', parseInt(e.target.value) || 0)}
            min="0"
          />
        </FormField>
      </div>

      <div className="form-section">
        <h4>权限控制</h4>
        
        <FormField label="需要粉丝牌" inline>
          <ToggleSwitch 
            checked={data.medal_check_enabled}
            onChange={(checked) => updateField('medal_check_enabled', checked)}
          />
        </FormField>

        {data.medal_check_enabled && (
          <FormField label="最低粉丝牌等级">
            <input
              type="number"
              className="form-input"
              value={data.min_medal_level || 0}
              onChange={(e) => updateField('min_medal_level', parseInt(e.target.value) || 0)}
              min="0"
              max="40"
            />
          </FormField>
        )}

        <FormField label="需要大航海" inline>
          <ToggleSwitch 
            checked={data.guard_check_enabled}
            onChange={(checked) => updateField('guard_check_enabled', checked)}
          />
        </FormField>

        {data.guard_check_enabled && (
          <FormField label="允许的大航海等级">
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={data.allowed_guard_levels?.includes(3)}
                  onChange={(e) => {
                    const levels = data.allowed_guard_levels || [];
                    if (e.target.checked) {
                      updateField('allowed_guard_levels', [...levels, 3]);
                    } else {
                      updateField('allowed_guard_levels', levels.filter(l => l !== 3));
                    }
                  }}
                />
                舰长
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={data.allowed_guard_levels?.includes(2)}
                  onChange={(e) => {
                    const levels = data.allowed_guard_levels || [];
                    if (e.target.checked) {
                      updateField('allowed_guard_levels', [...levels, 2]);
                    } else {
                      updateField('allowed_guard_levels', levels.filter(l => l !== 2));
                    }
                  }}
                />
                提督
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={data.allowed_guard_levels?.includes(1)}
                  onChange={(e) => {
                    const levels = data.allowed_guard_levels || [];
                    if (e.target.checked) {
                      updateField('allowed_guard_levels', [...levels, 1]);
                    } else {
                      updateField('allowed_guard_levels', levels.filter(l => l !== 1));
                    }
                  }}
                />
                总督
              </label>
            </div>
          </FormField>
        )}
      </div>

      <div className="form-section">
        <div className="section-header">
          <h4>关键词回复</h4>
          <button className="btn btn-sm btn-success" onClick={addKeyword}>
            ➕ 添加关键词
          </button>
        </div>
        
        <div className="keyword-list">
          {Object.entries(data.keywords || {}).map(([key, value], index) => (
            <div key={index} className="keyword-item">
              <input
                type="text"
                className="form-input keyword-key"
                value={key}
                onChange={(e) => updateKeyword(key, e.target.value, value)}
                placeholder="关键词"
              />
              <span className="keyword-arrow">→</span>
              <input
                type="text"
                className="form-input keyword-value"
                value={value}
                onChange={(e) => updateKeyword(key, key, e.target.value)}
                placeholder="回复内容"
              />
              <button 
                className="btn btn-sm btn-danger"
                onClick={() => removeKeyword(key)}
              >
                ✕
              </button>
            </div>
          ))}
          {Object.keys(data.keywords || {}).length === 0 && (
            <p className="help-text">暂无关键词，点击上方按钮添加</p>
          )}
        </div>
        <p className="help-text">💡 可用变量: {'{user}'} (用户昵称)</p>
      </div>
    </div>
  );
};

// Welcome Form Component
const WelcomeForm = ({ data, onChange }) => {
  const updateField = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="config-form">
      <div className="form-section">
        <h4>基础设置</h4>
        
        <FormField label="启用状态" inline>
          <ToggleSwitch 
            checked={data.enabled}
            onChange={(checked) => updateField('enabled', checked)}
          />
        </FormField>

        <FormField label="仅在开播时启用" inline>
          <ToggleSwitch 
            checked={data.live_only}
            onChange={(checked) => updateField('live_only', checked)}
          />
        </FormField>

        <FormField label="欢迎模板">
          <textarea
            className="form-input"
            value={data.template || ''}
            onChange={(e) => updateField('template', e.target.value)}
            placeholder="欢迎{user}肥来！"
            rows="3"
          />
          <p className="help-text">💡 可用变量: {'{user}'} (用户昵称), {'{medal_name}'} (粉丝牌名), {'{medal_level}'} (粉丝牌等级), {'{guard_level}'} (大航海等级)</p>
        </FormField>

        <FormField label="冷却时间（秒）">
          <input
            type="number"
            className="form-input"
            value={data.cooldown || 0}
            onChange={(e) => updateField('cooldown', parseInt(e.target.value) || 0)}
            min="0"
          />
        </FormField>
      </div>

      <div className="form-section">
        <h4>权限控制</h4>
        
        <FormField label="需要粉丝牌" inline>
          <ToggleSwitch 
            checked={data.medal_check_enabled}
            onChange={(checked) => updateField('medal_check_enabled', checked)}
          />
        </FormField>

        {data.medal_check_enabled && (
          <FormField label="最低粉丝牌等级">
            <input
              type="number"
              className="form-input"
              value={data.min_medal_level || 0}
              onChange={(e) => updateField('min_medal_level', parseInt(e.target.value) || 0)}
              min="0"
              max="40"
            />
          </FormField>
        )}

        <FormField label="需要大航海" inline>
          <ToggleSwitch 
            checked={data.guard_check_enabled}
            onChange={(checked) => updateField('guard_check_enabled', checked)}
          />
        </FormField>

        {data.guard_check_enabled && (
          <FormField label="允许的大航海等级">
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={data.allowed_guard_levels?.includes(3)}
                  onChange={(e) => {
                    const levels = data.allowed_guard_levels || [];
                    if (e.target.checked) {
                      updateField('allowed_guard_levels', [...levels, 3]);
                    } else {
                      updateField('allowed_guard_levels', levels.filter(l => l !== 3));
                    }
                  }}
                />
                舰长
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={data.allowed_guard_levels?.includes(2)}
                  onChange={(e) => {
                    const levels = data.allowed_guard_levels || [];
                    if (e.target.checked) {
                      updateField('allowed_guard_levels', [...levels, 2]);
                    } else {
                      updateField('allowed_guard_levels', levels.filter(l => l !== 2));
                    }
                  }}
                />
                提督
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={data.allowed_guard_levels?.includes(1)}
                  onChange={(e) => {
                    const levels = data.allowed_guard_levels || [];
                    if (e.target.checked) {
                      updateField('allowed_guard_levels', [...levels, 1]);
                    } else {
                      updateField('allowed_guard_levels', levels.filter(l => l !== 1));
                    }
                  }}
                />
                总督
              </label>
            </div>
          </FormField>
        )}
      </div>
    </div>
  );
};

// Gift Thank Form Component
const GiftThankForm = ({ data, onChange }) => {
  const updateField = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="config-form">
      <div className="form-section">
        <h4>基础设置</h4>
        
        <FormField label="启用状态" inline>
          <ToggleSwitch 
            checked={data.enabled}
            onChange={(checked) => updateField('enabled', checked)}
          />
        </FormField>

        <FormField label="仅在开播时启用" inline>
          <ToggleSwitch 
            checked={data.live_only}
            onChange={(checked) => updateField('live_only', checked)}
          />
        </FormField>

        <FormField label="感谢模板">
          <textarea
            className="form-input"
            value={data.template || ''}
            onChange={(e) => updateField('template', e.target.value)}
            placeholder="感谢{user}投喂的{num}个{gift}~"
            rows="3"
          />
          <p className="help-text">💡 可用变量: {'{user}'}, {'{gift}'} (礼物名), {'{num}'} (数量), {'{value}'} (价值)</p>
        </FormField>

        <FormField label="盲盒赚钱模板">
          <textarea
            className="form-input"
            value={data.template_blind_box_profit || ''}
            onChange={(e) => updateField('template_blind_box_profit', e.target.value)}
            placeholder="感谢{user}开出{gift}，赚了{profit}电池~欧皇附体！"
            rows="3"
          />
          <p className="help-text">💡 额外变量: {'{profit}'} (盈利电池数), {'{revenue}'} (产出), {'{cost}'} (成本)</p>
        </FormField>

        <FormField label="盲盒亏本模板">
          <textarea
            className="form-input"
            value={data.template_blind_box_loss || ''}
            onChange={(e) => updateField('template_blind_box_loss', e.target.value)}
            placeholder="感谢{user}开出{gift}，亏了{profit}电池~下次一定回本！"
            rows="3"
          />
        </FormField>

        <FormField label="最低感谢价值（电池）">
          <input
            type="number"
            className="form-input"
            value={data.min_total_coin || 0}
            onChange={(e) => updateField('min_total_coin', parseInt(e.target.value) || 0)}
            min="0"
          />
          <p className="help-text">低于此价值的礼物不会触发感谢</p>
        </FormField>

        <FormField label="冷却时间（秒）">
          <input
            type="number"
            className="form-input"
            value={data.cooldown || 0}
            onChange={(e) => updateField('cooldown', parseInt(e.target.value) || 0)}
            min="0"
          />
        </FormField>
      </div>
    </div>
  );
};

// Guard Thank Form Component
const GuardThankForm = ({ data, onChange }) => {
  const updateField = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="config-form">
      <div className="form-section">
        <h4>基础设置</h4>
        
        <FormField label="启用状态" inline>
          <ToggleSwitch 
            checked={data.enabled}
            onChange={(checked) => updateField('enabled', checked)}
          />
        </FormField>

        <FormField label="仅在开播时启用" inline>
          <ToggleSwitch 
            checked={data.live_only}
            onChange={(checked) => updateField('live_only', checked)}
          />
        </FormField>

        <FormField label="感谢模板">
          <textarea
            className="form-input"
            value={data.template || ''}
            onChange={(e) => updateField('template', e.target.value)}
            placeholder="感谢 {user} 开通了 {num} 个{unit}的{guard_name} ~老板大气！"
            rows="3"
          />
          <p className="help-text">💡 可用变量: {'{user}'}, {'{guard_name}'} (舰长/提督/总督), {'{num}'} (月数), {'{unit}'} (单位), {'{guard_level}'} (等级数字)</p>
        </FormField>

        <FormField label="冷却时间（秒）">
          <input
            type="number"
            className="form-input"
            value={data.cooldown || 0}
            onChange={(e) => updateField('cooldown', parseInt(e.target.value) || 0)}
            min="0"
          />
        </FormField>
      </div>
    </div>
  );
};

// Periodic Messages Form Component
const PeriodicMessagesForm = ({ data, onChange }) => {
  const updateField = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const addTask = () => {
    const tasks = [...(data.tasks || [])];
    tasks.push({
      interval: 600,
      messages: ['新消息']
    });
    updateField('tasks', tasks);
  };

  const updateTask = (index, field, value) => {
    const tasks = [...(data.tasks || [])];
    tasks[index] = { ...tasks[index], [field]: value };
    updateField('tasks', tasks);
  };

  const removeTask = (index) => {
    const tasks = [...(data.tasks || [])];
    tasks.splice(index, 1);
    updateField('tasks', tasks);
  };

  const addMessage = (taskIndex) => {
    const tasks = [...(data.tasks || [])];
    tasks[taskIndex].messages = [...(tasks[taskIndex].messages || []), '新消息'];
    updateField('tasks', tasks);
  };

  const updateMessage = (taskIndex, msgIndex, value) => {
    const tasks = [...(data.tasks || [])];
    tasks[taskIndex].messages[msgIndex] = value;
    updateField('tasks', tasks);
  };

  const removeMessage = (taskIndex, msgIndex) => {
    const tasks = [...(data.tasks || [])];
    tasks[taskIndex].messages.splice(msgIndex, 1);
    updateField('tasks', tasks);
  };

  return (
    <div className="config-form">
      <div className="form-section">
        <h4>基础设置</h4>
        
        <FormField label="启用状态" inline>
          <ToggleSwitch 
            checked={data.enabled}
            onChange={(checked) => updateField('enabled', checked)}
          />
        </FormField>

        <FormField label="仅在开播时启用" inline>
          <ToggleSwitch 
            checked={data.live_only}
            onChange={(checked) => updateField('live_only', checked)}
          />
        </FormField>
      </div>

      <div className="form-section">
        <div className="section-header">
          <h4>定时任务</h4>
          <button className="btn btn-sm btn-success" onClick={addTask}>
            ➕ 添加任务
          </button>
        </div>

        <div className="task-list">
          {(data.tasks || []).map((task, taskIndex) => (
            <div key={taskIndex} className="task-item">
              <div className="task-header">
                <h5>任务 {taskIndex + 1}</h5>
                <button 
                  className="btn btn-sm btn-danger"
                  onClick={() => removeTask(taskIndex)}
                >
                  🗑️ 删除任务
                </button>
              </div>

              <FormField label="发送间隔（秒）">
                <input
                  type="number"
                  className="form-input"
                  value={task.interval || 600}
                  onChange={(e) => updateTask(taskIndex, 'interval', parseInt(e.target.value) || 600)}
                  min="60"
                />
                <p className="help-text">Bot 会每隔这么多秒随机发送一条消息</p>
              </FormField>

              <FormField label="消息列表">
                <div className="message-list">
                  {(task.messages || []).map((msg, msgIndex) => (
                    <div key={msgIndex} className="message-item">
                      <textarea
                        className="form-input"
                        value={msg}
                        onChange={(e) => updateMessage(taskIndex, msgIndex, e.target.value)}
                        placeholder="输入要发送的消息"
                        rows="2"
                      />
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => removeMessage(taskIndex, msgIndex)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button 
                    className="btn btn-sm btn-success"
                    onClick={() => addMessage(taskIndex)}
                  >
                    ➕ 添加消息
                  </button>
                </div>
                <p className="help-text">Bot 会从这些消息中随机选择一条发送</p>
              </FormField>
            </div>
          ))}
          {(data.tasks || []).length === 0 && (
            <p className="help-text">暂无任务，点击上方按钮添加</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Form Field Component
const FormField = ({ label, children, inline }) => {
  return (
    <div className={`form-field ${inline ? 'form-field-inline' : ''}`}>
      <label className="form-label">{label}</label>
      <div className="form-control">
        {children}
      </div>
    </div>
  );
};

// Toggle Switch Component (standalone)
const ToggleSwitch = ({ checked, onChange }) => {
  return (
    <label className="toggle-switch">
      <input 
        type="checkbox" 
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="toggle-slider"></span>
    </label>
  );
};

// Nickname Form Component
const NicknameForm = ({ data, onChange }) => {
  const updateField = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const addNickname = () => {
    const nicknames = { ...data.nicknames };
    nicknames['输入UID'] = '输入昵称';
    updateField('nicknames', nicknames);
  };

  const updateNickname = (oldUid, newUid, nickname) => {
    const nicknames = { ...data.nicknames };
    if (oldUid !== newUid) {
      delete nicknames[oldUid];
    }
    nicknames[newUid] = nickname;
    updateField('nicknames', nicknames);
  };

  const removeNickname = (uid) => {
    const nicknames = { ...data.nicknames };
    delete nicknames[uid];
    updateField('nicknames', nicknames);
  };

  return (
    <div className="config-form">
      <div className="form-section">
        <h4>基础设置</h4>
        
        <FormField label="启用状态" inline>
          <ToggleSwitch 
            checked={data.enabled}
            onChange={(checked) => updateField('enabled', checked)}
          />
        </FormField>

        <FormField label="仅在开播时启用" inline>
          <ToggleSwitch 
            checked={data.live_only}
            onChange={(checked) => updateField('live_only', checked)}
          />
        </FormField>
      </div>

      <div className="form-section">
        <h4>权限控制</h4>
        
        <FormField label="需要粉丝牌" inline>
          <ToggleSwitch 
            checked={data.medal_check_enabled}
            onChange={(checked) => updateField('medal_check_enabled', checked)}
          />
        </FormField>

        {data.medal_check_enabled && (
          <FormField label="最低粉丝牌等级">
            <input
              type="number"
              className="form-input"
              value={data.min_medal_level || 0}
              onChange={(e) => updateField('min_medal_level', parseInt(e.target.value) || 0)}
              min="0"
              max="40"
            />
          </FormField>
        )}

        <FormField label="需要大航海" inline>
          <ToggleSwitch 
            checked={data.guard_check_enabled}
            onChange={(checked) => updateField('guard_check_enabled', checked)}
          />
        </FormField>

        {data.guard_check_enabled && (
          <FormField label="允许的大航海等级">
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={data.allowed_guard_levels?.includes(3)}
                  onChange={(e) => {
                    const levels = data.allowed_guard_levels || [];
                    if (e.target.checked) {
                      updateField('allowed_guard_levels', [...levels, 3]);
                    } else {
                      updateField('allowed_guard_levels', levels.filter(l => l !== 3));
                    }
                  }}
                />
                舰长
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={data.allowed_guard_levels?.includes(2)}
                  onChange={(e) => {
                    const levels = data.allowed_guard_levels || [];
                    if (e.target.checked) {
                      updateField('allowed_guard_levels', [...levels, 2]);
                    } else {
                      updateField('allowed_guard_levels', levels.filter(l => l !== 2));
                    }
                  }}
                />
                提督
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={data.allowed_guard_levels?.includes(1)}
                  onChange={(e) => {
                    const levels = data.allowed_guard_levels || [];
                    if (e.target.checked) {
                      updateField('allowed_guard_levels', [...levels, 1]);
                    } else {
                      updateField('allowed_guard_levels', levels.filter(l => l !== 1));
                    }
                  }}
                />
                总督
              </label>
            </div>
          </FormField>
        )}

        <p className="help-text">💡 权限控制：只有满足条件的用户才能使用昵称功能</p>
      </div>
    </div>
  );
};

// Gift Query Form Component
const GiftQueryForm = ({ data, onChange }) => {
  const updateField = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="config-form">
      <div className="form-section">
        <h4>基础设置</h4>
        
        <FormField label="启用状态" inline>
          <ToggleSwitch 
            checked={data.enabled}
            onChange={(checked) => updateField('enabled', checked)}
          />
        </FormField>

        <FormField label="仅在开播时启用" inline>
          <ToggleSwitch 
            checked={data.live_only}
            onChange={(checked) => updateField('live_only', checked)}
          />
        </FormField>
      </div>

      <div className="form-section">
        <h4>回复模板</h4>
        
        <FormField label="有记录时的模板">
          <textarea
            className="form-input"
            value={data.template_total || ''}
            onChange={(e) => updateField('template_total', e.target.value)}
            placeholder="{user} {time_label}共投喂了{num}个{gift}，共{value}电池"
            rows="3"
          />
          <p className="help-text">💡 可用变量: {'{user}'} (用户), {'{time_label}'} (时间标签), {'{num}'} (数量), {'{gift}'} (礼物名), {'{value}'} (总价值)</p>
        </FormField>

        <FormField label="无记录时的模板">
          <textarea
            className="form-input"
            value={data.template_zero || ''}
            onChange={(e) => updateField('template_zero', e.target.value)}
            placeholder="{user} {time_label}没有投喂过{gift}呢"
            rows="3"
          />
          <p className="help-text">💡 用户查询的礼物没有投喂记录时使用此模板</p>
        </FormField>
      </div>

      <div className="form-section">
        <h4>使用说明</h4>
        <p className="help-text">📖 用户可以发送"查询我的+时间+礼物名"来查询投喂记录，例如："查询我的今天小心心"、"查询我的本月辣条"</p>
        <p className="help-text">📖 Bot会自动识别时间范围，支持："今天查"、"本月查"、"今年查"等</p>
      </div>
    </div>
  );
};

// Add Room Modal Component
const AddRoomModal = ({ onClose, onAdd }) => {
  const [roomId, setRoomId] = useState('');
  const [error, setError] = useState('');

  const handleAdd = () => {
    if (!roomId || isNaN(roomId)) {
      setError('请输入有效的主播 UID');
      return;
    }
    onAdd(roomId);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>添加新房间</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-group">
            <label>主播 UID:</label>
            <input
              type="text"
              className="form-input"
              placeholder="请输入 B 站主播 UID"
              value={roomId}
              onChange={(e) => {
                setRoomId(e.target.value);
                setError('');
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
            />
            <p className="help-text">💡 主播 UID 可在 B 站个人空间 URL 中找到，如 space.bilibili.com/123456 中的 123456</p>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            取消
          </button>
          <button className="btn btn-success" onClick={handleAdd}>
            添加
          </button>
        </div>
      </div>
    </div>
  );
};

// Fans Medal Upgrade Form Component
const FansMedalUpgradeForm = ({ data, onChange }) => {
  const updateField = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="feature-form">
      <div className="form-section">
        <h4>📬 私信通知设置</h4>

        <FormField label="私信通知主播" inline>
          <ToggleSwitch 
            checked={data.enabled || false}
            onChange={(checked) => updateField('enabled', checked)}
          />
        </FormField>
        <p className="help-text">开启后，下播时粉丝牌变动信息会通过B站私信发送给主播</p>
      </div>

      <div className="form-section">
        <h4>🔍 过滤设置</h4>

        <FormField label="最低等级过滤">
          <input
            type="number"
            className="form-input form-input-small"
            value={data.min_level_filter || 0}
            onChange={(e) => updateField('min_level_filter', parseInt(e.target.value) || 0)}
            min="0"
            max="40"
          />
        </FormField>
        <p className="help-text">只报告达到此等级及以上的粉丝牌变动，设为 0 表示不过滤</p>
        <p className="help-text">💡 例如设为 10，则只报告升到 10 级及以上的粉丝</p>

        <FormField label="新粉丝等级阈值">
          <input
            type="number"
            className="form-input form-input-small"
            value={data.new_fan_level_threshold ?? 10}
            onChange={(e) => updateField('new_fan_level_threshold', parseInt(e.target.value) || 0)}
            min="0"
            max="40"
          />
        </FormField>
        <p className="help-text">超过此等级的用户若无历史记录，将被视为"重新点亮"而非"新加入"</p>
        <p className="help-text">💡 高等级用户不太可能是真正的新粉丝，可能是之前没有记录到</p>
      </div>

      <div className="form-section">
        <h4>💡 功能说明</h4>
        <p className="help-text">🏅 系统会在每次下播时自动检测粉丝牌变动（升级、新加入、重新点亮）</p>
        <p className="help-text">📬 <strong>私信通知</strong>：通过上方开关控制是否发送B站私信给主播</p>
        <p className="help-text">📊 <strong>直播报告</strong>：在 QQ推送配置 → 下播通知 → 报告配置 中控制是否在直播报告图片中显示</p>
      </div>
    </div>
  );
};

// Leaderboard PM Form Component
const LeaderboardPmForm = ({ data, onChange }) => {
  const updateField = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="feature-form">
      <div className="form-section">
        <h4>📊 贡献榜私信设置</h4>

        <FormField label="下播时私信发送贡献榜给主播" inline>
          <ToggleSwitch 
            checked={data.enabled || false}
            onChange={(checked) => updateField('enabled', checked)}
          />
        </FormField>
        <p className="help-text">开启后，每次下播时会自动生成贡献榜并通过B站私信发送给主播</p>
      </div>

      <div className="form-section">
        <h4>📤 发送格式</h4>

        <FormField label="发送贡献榜图片" inline>
          <ToggleSwitch 
            checked={data.send_image !== false}
            onChange={(checked) => updateField('send_image', checked)}
          />
        </FormField>
        <p className="help-text">按用户总贡献排序的榜单图片，显示每个用户的总电池数</p>

        <FormField label="发送总榜截图" inline>
          <ToggleSwitch 
            checked={data.send_mixed_leaderboard || false}
            onChange={(checked) => updateField('send_mixed_leaderboard', checked)}
          />
        </FormField>
        <p className="help-text">按用户+礼物分组的详细榜单，显示每个用户送了什么礼物和数量</p>

        <FormField label="发送文字版本" inline>
          <ToggleSwitch 
            checked={data.send_text || false}
            onChange={(checked) => updateField('send_text', checked)}
          />
        </FormField>
        <p className="help-text">发送纯文字格式的贡献榜，方便复制和编辑</p>
        <p className="help-text">💡 发送顺序：文字版 → 贡献榜图片 → 总榜截图</p>
      </div>

      <div className="form-section">
        <h4>🔍 过滤设置</h4>

        <FormField label="最低电池数">
          <input
            type="number"
            className="form-input form-input-small"
            value={data.min_battery || 0}
            onChange={(e) => updateField('min_battery', parseInt(e.target.value) || 0)}
            min="0"
          />
        </FormField>
        <p className="help-text">只显示贡献达到此电池数的用户，设为 0 表示不过滤</p>
        <p className="help-text">💡 例如设为 100，则只显示投喂了 100 电池及以上的用户</p>
      </div>

      <div className="form-section">
        <h4>💡 功能说明</h4>
        <p className="help-text">📊 <strong>贡献榜</strong>：按用户统计的投喂排行，显示每个用户的总电池数</p>
        <p className="help-text">🎁 <strong>总榜</strong>：按用户+礼物分组，显示每个用户送了哪些礼物、每种礼物多少个</p>
        <p className="help-text">📬 通过B站私信发送给主播，方便主播查看和感谢投喂的观众</p>
        <p className="help-text">⏰ 统计范围：本场开播到下播期间的所有礼物</p>
      </div>
    </div>
  );
};

export default BotConfig;
